/**
 * Theme Manager - Custom implementation for Moron Mayhem
 *
 * Provides theme persistence, switching, and observing functionality
 * with specific optimizations for Livewire SPA navigation.
 */
export class ThemeManager {
  /**
   * Storage key for theme persistence
   * @type {string}
   */
  static STORAGE_KEY = 'theme';

  /**
   * Default theme to use if none is stored
   * @type {string}
   */
  static DEFAULT_THEME = 'dark';

  /**
   * List of registered observers to notify on theme change
   * @type {Array<Function>}
   */
  static observers = [];

  /**
   * Initialize theme manager and set up event listeners
   */
  static initialize() {
    // Apply theme on initial page load
    this.applyTheme();

    // Set up Livewire navigation listener for SPA persistence
    document.addEventListener('livewire:navigated', () => {
      this.applyTheme();
    });

    // Create a MutationObserver to watch for theme attribute changes
    // This ensures consistency even if the data-theme is changed directly
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (
          mutation.attributeName === 'data-theme' &&
          mutation.target === document.documentElement
        ) {
          const newTheme = document.documentElement.getAttribute('data-theme');
          if (newTheme && newTheme !== this.getTheme()) {
            localStorage.setItem(this.STORAGE_KEY, newTheme);
            this.notifyObservers(newTheme);
          }
        }
      });
    });

    // Start observing the document for theme changes
    observer.observe(document.documentElement, { attributes: true });
  }

  /**
   * Get the current theme from storage or use default
   * @returns {string} Current theme name
   */
  static getTheme() {
    return localStorage.getItem(this.STORAGE_KEY) || this.DEFAULT_THEME;
  }

  /**
   * Set a new theme and persist it
   * @param {string} theme - Theme name to set
   */
  static setTheme(theme) {
    if (!theme) return;

    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(this.STORAGE_KEY, theme);
    this.notifyObservers(theme);
  }

  /**
   * Apply the saved theme from storage
   */
  static applyTheme() {
    const theme = this.getTheme();
    document.documentElement.setAttribute('data-theme', theme);
  }

  /**
   * Register an observer to be notified when theme changes
   * @param {Function} callback - Function to call on theme change
   * @returns {Function} Unsubscribe function
   */
  static observe(callback) {
    this.observers.push(callback);

    // Return unsubscribe function
    return () => {
      this.observers = this.observers.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all registered observers about a theme change
   * @param {string} theme - New theme name
   */
  static notifyObservers(theme) {
    this.observers.forEach(callback => {
      try {
        callback(theme);
      } catch (err) {
        console.error('Error in theme observer:', err);
      }
    });
  }
}
